import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import ActivityScreen from "../../components/screens/Activity/ActivityScreen";
import {
  completions as completionQuerys,
  users as userQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import { useMemberProgress } from "@nualang/nualang-api-and-queries/Queries/progress";
import { DateTime } from "luxon";
import { getMemberCompletions } from "@nualang/nualang-api-and-queries/APIs/Completions";
import { getMemberProgress } from "@nualang/nualang-api-and-queries/APIs/Progress";
import { getUser } from "@nualang/nualang-api-and-queries/APIs/Users";
import {
  completions as completionHelpers,
  progress as progressHelpers,
} from "../../utils/helpers";
import config from "../../config";
import { useSubscription } from "../../hooks";

function Activity(props) {
  const {
    authData: { username, attributes },
    handleViewExerciseAttempt,
  } = props;
  const params = useParams();
  const { memberId, courseId, sectionId, topicId } = params;
  const { subscription } = useSubscription();
  const [fromDate, setFromDate] = React.useState(
    DateTime.now().minus({ years: 1 }),
  );
  const [toDate, setToDate] = React.useState(DateTime.now());

  const { t } = useTranslation();

  const userId = memberId || username;

  const memberCompletionsQuery = completionQuerys.useMemberCompletions(
    async (memberId, { fromDate, toDate }) => {
      const memberCompletions = await getMemberCompletions(memberId);
      let formattedCompletions = await completionHelpers.formatCompletions(
        memberCompletions.Items,
        memberId,
        attributes.preferred_username,
      );
      if (fromDate) {
        formattedCompletions = formattedCompletions.filter(
          (item) =>
            item.createdAt >= fromDate.toJSDate().getTime() &&
            item.createdAt <= toDate.toJSDate().getTime(),
        );
      }
      if (courseId || sectionId || topicId) {
        formattedCompletions = formattedCompletions.filter((c) => {
          if (courseId && sectionId && topicId) {
            return (
              c.courseId === courseId &&
              c.sectionId === sectionId &&
              c.topicId === topicId
            );
          }
          if (courseId && sectionId) {
            return c.courseId === courseId && c.sectionId === sectionId;
          }
          if (courseId) {
            return c.courseId === courseId;
          }
          return true;
        });
      }
      return formattedCompletions;
    },
    {
      memberId: userId,
      filters: {
        fromDate,
        toDate,
      },
    },
    { enabled: !!userId },
  );

  const memberProgressQuery = useMemberProgress(
    async () => {
      const memberProgress = await getMemberProgress(userId);
      let formattedProgress = await progressHelpers.formatProgress(
        memberProgress.Items,
        userId,
        attributes.preferred_username,
      );
      if (fromDate) {
        formattedProgress = formattedProgress.filter(
          (item) =>
            item.createdAt >= fromDate.toJSDate().getTime() &&
            item.createdAt <= toDate.toJSDate().getTime(),
        );
      }
      if (courseId || sectionId || topicId) {
        formattedProgress = formattedProgress.filter((c) => {
          if (courseId && sectionId && topicId) {
            return (
              c.courseId === courseId &&
              c.sectionId === sectionId &&
              c.topicId === topicId
            );
          }
          if (courseId && sectionId) {
            return c.courseId === courseId && c.sectionId === sectionId;
          }
          if (courseId) {
            return c.courseId === courseId;
          }
          return true;
        });
      }
      return formattedProgress;
    },
    {
      memberId: userId,
      filters: {
        fromDate,
        toDate,
      },
    },
    { enabled: !!userId },
  );

  const userQuery = userQuerys.useUser(
    async (userId) => {
      const response = await getUser(userId, memberId ? true : false);
      return response;
    },
    {
      userId,
    },
    {
      enabled: !!userId,
    },
  );

  const memberInfo =
    userQuery.isSuccess && userQuery.data ? userQuery.data : {};

  const fromDateChange = (date) => {
    setFromDate(date);
  };

  const toDateChange = (date) => {
    setToDate(date);
  };
  return (
    <ActivityScreen
      t={t}
      fromDate={fromDate}
      toDate={toDate}
      toDateChange={toDateChange}
      fromDateChange={fromDateChange}
      completions={
        memberCompletionsQuery.isSuccess &&
        Array.isArray(memberCompletionsQuery.data) &&
        memberCompletionsQuery.data.length
          ? memberCompletionsQuery.data
          : []
      }
      completionsTotal={
        memberCompletionsQuery.isSuccess &&
        Array.isArray(memberCompletionsQuery.data) &&
        memberCompletionsQuery.data.length
          ? memberCompletionsQuery.data.length
          : 0
      }
      progress={
        memberProgressQuery.isSuccess &&
        Array.isArray(memberProgressQuery.data) &&
        memberProgressQuery.data.length
          ? memberProgressQuery.data
          : []
      }
      progressTotal={
        memberProgressQuery.isSuccess &&
        Array.isArray(memberProgressQuery.data) &&
        memberProgressQuery.data.length
          ? memberProgressQuery.data.length
          : 0
      }
      isCompletionsLoading={memberCompletionsQuery.isLoading}
      isProgressLoading={memberProgressQuery.isLoading}
      handleViewEvent={handleViewExerciseAttempt}
      subscription={subscription}
      name={
        memberInfo && memberInfo.UserAttributes
          ? memberInfo.UserAttributes.preferred_username
          : null
      }
      userImage={
        memberInfo && memberInfo.UserAttributes
          ? memberInfo.UserAttributes.picture
          : null
      }
      mainSiteUrl={config.SITE_URL}
      verificationStatus={
        memberInfo.verificationStatus ? memberInfo.verificationStatus : ""
      }
      gameAward={memberInfo.gameAward ? memberInfo.gameAward : ""}
    />
  );
}

Activity.propTypes = {
  authData: PropTypes.object,
  themeContext: PropTypes.object,
  handleViewExerciseAttempt: PropTypes.func,
};

export default Activity;
